import {
  Component,
  OnDestroy,
  OnInit,
  WritableSignal,
  computed,
  inject,
  signal
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { lastValueFrom, map } from 'rxjs';
import {
  IntegrationControllerService,
  LoggerUpdatedShippingCompanyRangeDTO,
  ShippingCompanyRangeDTO,
  WeightRangeShippingCompanyRange
} from 'src/app/admin-api';
import { ShippingCompany } from 'src/app/admin-api/model/shippingCompany';
import { TableColumn } from 'src/app/components/table';
import { ZipRange } from 'src/app/models/enums/ZipRange';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
  selector: 'app-update-shipping-company',
  templateUrl: './update-shipping-company.component.html',
  styleUrl: './update-shipping-company.component.scss'
})
export class UpdateShippingCompanyComponent implements OnInit, OnDestroy {
  private integrationService: IntegrationControllerService = inject(
    IntegrationControllerService
  );
  private title = inject(Title);

  weightRangeShippingCompanyRange: WritableSignal<
    WeightRangeShippingCompanyRange[]
  > = signal(undefined);
  logger: LoggerUpdatedShippingCompanyRangeDTO[];
  range: ShippingCompanyRangeDTO[];
  shippingCompany: ShippingCompany[];
  zip: number = null;

  selectedShippingCompany: number | null = null;
  selectedZipStartRange: number = null;
  selectedZipEndRange: number = null;
  selectedOperationType: string = null;
  selectedOperationTypeFilterType: string = null;
  selectedExportOrigin: string = null;

  cancelButtonDisabled: boolean = true;
  syncButtonDisabled: boolean = true;
  updateButtonDisabled: boolean = true;
  rollbackButtonDisabled: boolean = true;
  continueButtonDisabled: boolean = true;
  stopButtonDisabled: boolean = true;

  private weightRangeIntervalId: any;
  private loggerIntervalId: any;

  zipRangeOptions = [
    {
      label: '1000000 até 9999999',
      value: { start: ZipRange.ZERO_START, end: ZipRange.ZERO_END }
    },
    {
      label: '10000000 até 19999999',
      value: { start: ZipRange.ONE_START, end: ZipRange.ONE_END }
    },
    {
      label: '20000000 até 29999999',
      value: { start: ZipRange.TWO_START, end: ZipRange.TWO_END }
    },
    {
      label: '30000000 até 39999999',
      value: { start: ZipRange.THREE_START, end: ZipRange.THREE_END }
    },
    {
      label: '40000000 até 49999999',
      value: { start: ZipRange.FOUR_START, end: ZipRange.FOUR_END }
    },
    {
      label: '50000000 até 59999999',
      value: { start: ZipRange.FIVE_START, end: ZipRange.FIVE_END }
    },
    {
      label: '60000000 até 69999999',
      value: { start: ZipRange.SIX_START, end: ZipRange.SIX_END }
    },
    {
      label: '70000000 até 79999999',
      value: { start: ZipRange.SEVEN_START, end: ZipRange.SEVEN_END }
    },
    {
      label: '80000000 até 89999999',
      value: { start: ZipRange.EIGHT_START, end: ZipRange.EIGHT_END }
    },
    {
      label: '90000000 até 99999999',
      value: { start: ZipRange.NINE_START, end: ZipRange.NINE_END }
    }
  ];

  operationTypes = [
    { label: 'PRODUÇÃO', value: 'PRODUCTION' },
    { label: 'BACKUP', value: 'BACKUP' },
    { label: 'SIMULATION', value: 'SIMULATION' }
  ];

  synchronism = computed(() =>
    this.weightRangeShippingCompanyRange()?.some(
      (item) => item.status === 'PENDING'
    )
  );

  async ngOnInit(): Promise<void> {
    this.title.setTitle(
      'Integrações - Atualização da tabela de frete de Assinatura'
    );

    await this.fetch();

    this.weightRangeIntervalId = setInterval(() => {
      this.fetchUpdateWeightRange();
    }, 12000);

    this.loggerIntervalId = setInterval(() => {
      this.fetchUpdateLogger();
    }, 15000);
  }

  ngOnDestroy(): void {
    if (this.weightRangeIntervalId) {
      clearInterval(this.weightRangeIntervalId);
    }
    if (this.loggerIntervalId) {
      clearInterval(this.loggerIntervalId);
    }
  }

  cols: Array<TableColumn> = [
    new TableColumn('Menor preço', 'lowerShippingPrice', true, 'text'),
    new TableColumn('Preço médio', 'averageShippingPrice', true, 'text'),
    new TableColumn('Maior preço', 'greaterShippingPrice', true, 'text'),
    new TableColumn('Menor peso', 'lowerWeight', true, 'text'),
    new TableColumn('Peso médio', 'averageWeight', true, 'text'),
    new TableColumn('Maior peso', 'greaterWeight', true, 'text'),
    new TableColumn(
      'Total de linhas',
      'totalShippingCompanyRange',
      true,
      'number'
    ),
    new TableColumn('Responsável', 'responsible', true, 'text'),
    new TableColumn('Ambiente', 'environment', true, 'text'),
    new TableColumn('Data da atualização', 'dateCreated', true, 'date')
  ];

  colsRange: Array<TableColumn> = [
    new TableColumn('Transportadora', 'shippingCompanyName', true, 'text'),
    new TableColumn('Estado', 'state', true, 'text'),
    new TableColumn('CEP inicial', 'zipStart', true, 'number'),
    new TableColumn('CEP final', 'zipEnd', true, 'number'),
    new TableColumn('Menor peso (gramas)', 'minGrams', true, 'number'),
    new TableColumn('Maior peso (gramas)', 'maxGrams', true, 'number'),
    new TableColumn('Valor do frete', 'shippingPrice', true, 'text'),
    new TableColumn('Dias para entrega', 'daysToDelivery', true, 'number')
  ];

  async fetch(): Promise<void> {
    try {
      LoaderService.showLoader();
      this.shippingCompany = await lastValueFrom(
        this.integrationService
          .getLogisticsShippingCompanies()
          .pipe(map((data) => data.result))
      );
      this.weightRangeShippingCompanyRange.set(
        await lastValueFrom(
          this.integrationService
            .findAllWeightRangeShippingCompanyRange()
            .pipe(map((data) => data.result))
        )
      );
      await this.buttonDisabled();
      this.logger = await lastValueFrom(
        this.integrationService
          .findLoggerUpdateShippingCompanyRange()
          .pipe(map((data) => data.result))
      );
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async fetchUpdateWeightRange(): Promise<void> {
    try {
      this.weightRangeShippingCompanyRange.set(
        await lastValueFrom(
          this.integrationService
            .findAllWeightRangeShippingCompanyRange()
            .pipe(map((data) => data.result))
        )
      );
      await this.buttonDisabled();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
  }

  async fetchUpdateLogger(): Promise<void> {
    try {
      this.logger = await lastValueFrom(
        this.integrationService
          .findLoggerUpdateShippingCompanyRange()
          .pipe(map((data) => data.result))
      );
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
  }

  async cancelUpdate(): Promise<void> {
    try {
      LoaderService.showLoader();
      await lastValueFrom(
        this.integrationService.abortUpdateShippingCompanyRange()
      );
      await this.fetchUpdateWeightRange();
      await this.fetchUpdateLogger();
      await this.buttonDisabled();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async stopUpdate(): Promise<void> {
    try {
      LoaderService.showLoader();
      await lastValueFrom(
        this.integrationService.stopUpdateShippingCompanyRange()
      );
      await this.fetchUpdateWeightRange();
      await this.fetchUpdateLogger();
      await this.buttonDisabled();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async continueUpdate(): Promise<void> {
    try {
      LoaderService.showLoader();
      await lastValueFrom(
        this.integrationService.continueSyncShippingCompanyRange()
      );
      await this.fetchUpdateWeightRange();
      await this.fetchUpdateLogger();
      await this.buttonDisabled();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async syncUpdateShippingCompanyRange(): Promise<void> {
    try {
      LoaderService.showLoader();
      await lastValueFrom(this.integrationService.syncShippingCompanyRange());
      await this.fetchUpdateWeightRange();
      await this.buttonDisabled();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async updateShippingCompanyRange(): Promise<void> {
    try {
      LoaderService.showLoader();
      await lastValueFrom(this.integrationService.updateShippingCompanyRange());
      await this.buttonDisabled();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async rollbackShippingCompanyRange(): Promise<void> {
    try {
      LoaderService.showLoader();
      await lastValueFrom(this.integrationService.rollback());
      await this.buttonDisabled();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async rollbackIsValid(): Promise<void> {
    try {
      this.rollbackButtonDisabled = !(await lastValueFrom(
        this.integrationService
          .rollbackIsValid()
          .pipe(map((data) => data.result))
      ));
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
  }

  async buttonDisabled() {
    await this.rollbackIsValid();

    this.cancelButtonDisabled = this.weightRangeShippingCompanyRange()?.every(
      (obj) =>
        obj.status === 'ABORT' ||
        obj.status === 'FINISH' ||
        obj.status === 'NOT_START' ||
        obj.status === 'FAIL'
    );

    this.continueButtonDisabled = !this.weightRangeShippingCompanyRange()?.some(
      (obj) => obj.status === 'STOP'
    );

    this.stopButtonDisabled = !this.weightRangeShippingCompanyRange()?.some(
      (obj) => obj.status === 'PENDING'
    );

    this.updateButtonDisabled = !this.weightRangeShippingCompanyRange()?.every(
      (obj) => obj.status === 'FINISH' && obj.inProduction === 0
    );

    this.syncButtonDisabled = this.weightRangeShippingCompanyRange()?.some(
      (obj) => obj.status === 'PENDING'
    );
    if (this.syncButtonDisabled) {
      return;
    }
    this.syncButtonDisabled = !this.weightRangeShippingCompanyRange()?.every(
      (obj) =>
        obj.status === 'ABORT' ||
        obj.status === 'FINISH' ||
        obj.status === 'FAIL'
    );
  }

  getGridClass(status: string, inProduction: number): string {
    switch (status) {
      case 'PENDING':
        return 'pending';
      case 'FAIL':
        return 'fail';
      case 'STOP':
        return 'stop';
      case 'ABORT':
        return 'abort';
      case 'FINISH':
        return inProduction === 0 ? 'finish' : 'finish_in_production';
      case 'NOT_START':
        return 'not_start';
      default:
        return '';
    }
  }

  getDescription(status: string, inProduction: number): string {
    switch (status) {
      case 'PENDING':
      case 'FAIL':
        return 'Falha';
      case 'STOP':
        return 'Sincronismo pausado';
      case 'ABORT':
        return 'Cancelado';
      case 'FINISH':
        return inProduction === 0
          ? 'Finalizado e Pendente de envio para Produção'
          : 'Finalizado e em Produção';
      case 'NOT_START':
        return 'Não iniciado';
      default:
        return '';
    }
  }

  async onShippingCompanyChange(event: any) {
    this.selectedShippingCompany = event.value?.shippingCompanyId;
    await this.checkAndFilterRange();
  }

  async onZipRangeChange(event: any) {
    this.selectedZipStartRange = event.value?.value?.start;
    this.selectedZipEndRange = event.value?.value?.end;
    await this.checkAndFilterRange();
  }

  async onOperationTypeChange(event: any) {
    this.selectedOperationType = event.value?.value;
    await this.checkAndFilterRange();
  }

  async checkAndFilterRange() {
    if (
      this.selectedShippingCompany &&
      this.selectedZipStartRange &&
      this.selectedZipEndRange &&
      this.selectedOperationType
    ) {
      try {
        LoaderService.showLoader();
        this.range = await lastValueFrom(
          this.integrationService
            .getShippingCompanyRanges(
              this.selectedShippingCompany,
              this.selectedZipStartRange,
              this.selectedZipEndRange,
              this.selectedOperationType
            )
            .pipe(map((data) => data.result))
        );
      } catch (error) {
        AppDialogService.showErrorDialog(error);
      } finally {
        LoaderService.showLoader(false);
      }
    }
  }

  async onZipChange(value: number) {
    this.zip = value;
    if (
      value &&
      (value.toString().length === 7 || value.toString().length === 8) &&
      this.selectedOperationTypeFilterType
    ) {
      await this.lookupZip();
    }
  }

  async onOperationTypeFilterTypeChange(event: any) {
    this.selectedOperationTypeFilterType = event.value?.value;
    if (this.zip && this.selectedOperationTypeFilterType) {
      await this.lookupZip();
    }
  }

  async lookupZip() {
    try {
      LoaderService.showLoader();
      this.range = await lastValueFrom(
        this.integrationService
          .getShippingCompanyRangesByZip(
            this.zip,
            this.selectedOperationTypeFilterType
          )
          .pipe(map((data) => data.result))
      );
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  onExportOriginChange(event: any) {
    this.selectedExportOrigin = event.value?.value;
  }

  async downloadShippingRateTable() {
    if (!this.selectedExportOrigin) {
      return;
    }

    try {
      LoaderService.showLoader();

      const response = await lastValueFrom(
        this.integrationService.exportShippingCompanyRangesToExcel(
          this.selectedExportOrigin,
          'response'
        )
      );

      const blob = response.body;

      if (blob && blob.type === 'application/json') {
        const text = await blob.text();

        try {
          const jsonData = JSON.parse(text);

          if (jsonData.result && typeof jsonData.result === 'string') {
            const byteCharacters = atob(jsonData.result);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);

            const excelBlob = new Blob([byteArray], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            const filename = this.generateFilename(this.selectedExportOrigin);
            this.downloadFile(excelBlob, filename);
            return;
          } else {
            AppDialogService.showErrorDialog({
              message: 'Erro: Resposta inválida da API'
            } as any);
            return;
          }
        } catch (parseError) {
          AppDialogService.showErrorDialog({
            message: 'Erro: Resposta inválida da API'
          } as any);
          return;
        }
      }

      if (blob) {
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = this.generateFilename(this.selectedExportOrigin);

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        const finalBlob = blob.type.includes('application/vnd.openxmlformats')
          ? blob
          : new Blob([blob], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

        this.downloadFile(finalBlob, filename);
      }
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  private generateFilename(origin: string): string {
    let filename = 'tabela-frete-aquisicao';
    if ('BACKUP'.toLowerCase() === origin?.toLowerCase()) {
      filename += '-backup';
    } else if ('SIMULATION'.toLowerCase() === origin?.toLowerCase()) {
      filename += '-simulacao';
    } else {
      filename += '-producao';
    }
    filename += '.xlsx';
    return filename;
  }

  private downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
  }
}
